import React, { useMemo, useRef, useState, useEffect } from 'react';

import currencyFormatter from 'currency-formatter';
import { FormikHelpers } from 'formik';

import { Container, Error } from 'components/Input/styles';
import useOutsideClick from 'hooks/useOutsideClick';
import { getCurrencyConfiguration } from 'utils/currency';

import { Input, InputContainer, Placeholder, Suffix } from './styles';

export interface Props {
  name?: string;
  value?: number;
  placeHolder?: string;
  darkPlaceholder?: boolean;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  suffix?: string;
  countryCurrency: string;
  formik?: FormikHelpers<number>;
  onChange?: (event: React.SyntheticEvent, maskValue: string, value: number) => void;
}

const currencyLocaleParts = (currency: string) => currencyFormatter.findCurrency(currency);

const CurrencyInput = ({
  name,
  value,
  placeHolder,
  darkPlaceholder,
  countryCurrency,
  error,
  disabled,
  formik,
  onChange,
}: Props) => {
  const currencyConfiguration = getCurrencyConfiguration(countryCurrency);

  const ref = useRef<HTMLDivElement>(null);

  const [showPlaceHolder, setShowPlaceHolder] = useState(true);
  const [isFocused, setIsFocused] = useState(false);

  const isEmptyValue = (value?: number | '') => value === null || value === undefined || value === 0 || value === '';

  useEffect(() => {
    if (isFocused) {
      setShowPlaceHolder(false);
      return;
    }

    if (showPlaceHolder && !isEmptyValue(value)) {
      setShowPlaceHolder(false);
      return;
    }

    if (!showPlaceHolder && isEmptyValue(value)) {
      setShowPlaceHolder(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isFocused]);

  useOutsideClick(ref, () => {
    setIsFocused(false);
  });

  const separators = useMemo(() => currencyLocaleParts(countryCurrency), [countryCurrency]);

  const formattedValue = useMemo(
    () => String(value).replace('.', separators.decimalSeparator),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, countryCurrency],
  );

  const scrollToInput = () => {
    setTimeout(
      () =>
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }),
      500,
    );
  };

  const handleFocus = () => {
    scrollToInput();
  };

  const handleBlur = () => {
    if (formik && name) {
      formik.setFieldTouched(name);
      setIsFocused(false);
    }
  };

  const handleChange = (event: React.SyntheticEvent, maskValue: string, floatValue: number) => {
    if (formik && name) {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, floatValue);
    } else if (onChange) {
      onChange(event, maskValue, floatValue);
    }
  };

  const showInput = () => {
    setIsFocused(true);
    document.getElementById(`${name}_input`)?.focus();
  };

  return (
    <div ref={ref} onFocus={showInput}>
      <Container styledProps={{ hasError: !!error }}>
        <InputContainer onClick={showInput}>
          {showPlaceHolder && <Placeholder $darkPlaceholder={darkPlaceholder}>{placeHolder}</Placeholder>}
          <Input
            id={`${name}_input`}
            name={name}
            value={formattedValue}
            disabled={disabled}
            inputType="tel"
            precision={currencyConfiguration.decimals.toString()}
            prefix={currencyConfiguration.symbol}
            thousandSeparator={separators.thousandsSeparator}
            decimalSeparator={separators.decimalSeparator}
            allowNegative={false}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChangeEvent={handleChange}
            maxLength={23}
            styledProps={{ hasError: Boolean(error) }}
          />
        </InputContainer>
        <Suffix>{currencyConfiguration.codeISO === 'BRL' ? 'R$' : countryCurrency}</Suffix>
      </Container>
      {error && <Error>{error}</Error>}
    </div>
  );
};

export default CurrencyInput;
